import { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Form, Input, InputNumber, Switch, Button, Row, Col,
  Divider, Space, message, Spin, Tag, Alert,
} from 'antd';
import {
  Calendar, Mail, Save, Send, ExternalLink, CheckCircle2, XCircle,
} from 'lucide-react';
import { integrationsApi } from '../../services/api';

const { Title, Text, Paragraph } = Typography;

interface SmtpConfig {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;
  fromName: string;
  fromEmail: string;
}

interface GoogleCalendarConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  refreshToken?: string;
  calendarId?: string;
}

export default function IntegrationsPage() {
  const [loading, setLoading] = useState(true);
  const [smtpForm] = Form.useForm<SmtpConfig>();
  const [gcalForm] = Form.useForm<GoogleCalendarConfig>();

  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [gcalEnabled, setGcalEnabled] = useState(false);
  const [gcalConnected, setGcalConnected] = useState(false);

  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingGcal, setSavingGcal] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [connectingGcal, setConnectingGcal] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await integrationsApi.list();
      const settings = data as any[];

      const smtp = settings.find((s: any) => s.key === 'smtp');
      if (smtp) {
        smtpForm.setFieldsValue(smtp.value);
        setSmtpEnabled(smtp.enabled);
      }

      const gcal = settings.find((s: any) => s.key === 'google_calendar');
      if (gcal) {
        gcalForm.setFieldsValue(gcal.value);
        setGcalEnabled(gcal.enabled);
        setGcalConnected(!!gcal.value?.refreshToken);
      }
    } catch {
      // Settings not found yet — that's fine
    } finally {
      setLoading(false);
    }
  }, [smtpForm, gcalForm]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // ── SMTP ──────────────────────────────────────

  const handleSaveSmtp = async () => {
    try {
      const values = await smtpForm.validateFields();
      setSavingSmtp(true);
      await integrationsApi.upsert('smtp', {
        value: values,
        enabled: smtpEnabled,
      });
      message.success('Configuración SMTP guardada');
    } catch (err: any) {
      if (err?.errorFields) return; // validation errors
      message.error('Error al guardar SMTP');
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleToggleSmtp = async (checked: boolean) => {
    setSmtpEnabled(checked);
    try {
      // Only toggle if the setting already exists
      const values = smtpForm.getFieldsValue();
      if (values.host) {
        await integrationsApi.upsert('smtp', { value: values, enabled: checked });
        message.success(checked ? 'SMTP activado' : 'SMTP desactivado');
      }
    } catch {
      message.error('Error al cambiar estado');
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const smtpValues = smtpForm.getFieldsValue();
      if (!smtpValues.fromEmail) {
        message.warning('Configura un email de remitente primero');
        return;
      }
      const { data } = await integrationsApi.testSmtp(smtpValues.fromEmail);
      if (data.success) {
        message.success('Email de prueba enviado correctamente');
      } else {
        message.error(`Error: ${data.error}`);
      }
    } catch (err: any) {
      message.error('Error al enviar email de prueba');
    } finally {
      setTestingSmtp(false);
    }
  };

  // ── Google Calendar ───────────────────────────

  const handleSaveGcal = async () => {
    try {
      const values = await gcalForm.validateFields();
      setSavingGcal(true);

      // Preserve existing refreshToken if present
      const existing = gcalForm.getFieldValue('refreshToken');
      await integrationsApi.upsert('google_calendar', {
        value: { ...values, refreshToken: existing || undefined },
        enabled: gcalEnabled,
      });
      message.success('Configuración de Google Calendar guardada');
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error('Error al guardar Google Calendar');
    } finally {
      setSavingGcal(false);
    }
  };

  const handleToggleGcal = async (checked: boolean) => {
    setGcalEnabled(checked);
    try {
      const values = gcalForm.getFieldsValue();
      if (values.clientId) {
        await integrationsApi.upsert('google_calendar', {
          value: values,
          enabled: checked,
        });
        message.success(checked ? 'Google Calendar activado' : 'Google Calendar desactivado');
      }
    } catch {
      message.error('Error al cambiar estado');
    }
  };

  const handleConnectGcal = async () => {
    setConnectingGcal(true);
    try {
      const { data } = await integrationsApi.getGoogleAuthUrl();
      if (data.url) {
        window.open(data.url, '_blank', 'width=600,height=700');
      } else {
        message.error(data.error || 'No se pudo generar la URL de autorización');
      }
    } catch {
      message.error('Error al conectar con Google Calendar');
    } finally {
      setConnectingGcal(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24, color: '#1F2F3F' }}>
        Integraciones
      </Title>

      <Row gutter={[24, 24]}>
        {/* ── SMTP Email Card ── */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <Mail size={20} style={{ color: '#cfb56b' }} />
                <span>Correo SMTP</span>
              </Space>
            }
            extra={
              <Switch
                checked={smtpEnabled}
                onChange={handleToggleSmtp}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            }
            styles={{ body: { paddingTop: 16 } }}
          >
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Configura un servidor SMTP para enviar auto-respuestas cuando alguien envíe un
              formulario desde la web (contacto, canal de denuncias, candidatura, etc.).
            </Paragraph>

            {smtpEnabled && (
              <Alert
                type="success"
                showIcon
                icon={<CheckCircle2 size={16} />}
                message="Auto-respuestas activadas"
                description="Se enviará un email de confirmación automática a cada persona que envíe un formulario."
                style={{ marginBottom: 16 }}
              />
            )}

            <Form form={smtpForm} layout="vertical" size="middle">
              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item
                    label="Servidor SMTP"
                    name="host"
                    rules={[{ required: true, message: 'Requerido' }]}
                  >
                    <Input placeholder="smtp.gmail.com" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Puerto"
                    name="port"
                    rules={[{ required: true, message: 'Requerido' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="587"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Conexión segura (SSL/TLS)"
                name="secure"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Usuario"
                    name="user"
                    rules={[{ required: true, message: 'Requerido' }]}
                  >
                    <Input placeholder="user@swomey.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Contraseña"
                    name="pass"
                    rules={[{ required: true, message: 'Requerido' }]}
                  >
                    <Input.Password placeholder="••••••••" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider style={{ margin: '12px 0' }} />

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Nombre del remitente"
                    name="fromName"
                    rules={[{ required: true, message: 'Requerido' }]}
                  >
                    <Input placeholder="Swomey" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email del remitente"
                    name="fromEmail"
                    rules={[
                      { required: true, message: 'Requerido' },
                      { type: 'email', message: 'Email inválido' },
                    ]}
                  >
                    <Input placeholder="info@swomey.com" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Divider style={{ margin: '12px 0' }} />

            <Space>
              <Button
                type="primary"
                icon={<Save size={14} />}
                loading={savingSmtp}
                onClick={handleSaveSmtp}
                style={{ background: '#1F2F3F' }}
              >
                Guardar
              </Button>
              <Button
                icon={<Send size={14} />}
                loading={testingSmtp}
                onClick={handleTestSmtp}
                disabled={!smtpEnabled}
              >
                Enviar email de prueba
              </Button>
            </Space>
          </Card>
        </Col>

        {/* ── Google Calendar Card ── */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <Calendar size={20} style={{ color: '#cfb56b' }} />
                <span>Google Calendar</span>
              </Space>
            }
            extra={
              <Switch
                checked={gcalEnabled}
                onChange={handleToggleGcal}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            }
            styles={{ body: { paddingTop: 16 } }}
          >
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Conecta Google Calendar para sincronizar reuniones automáticamente con tu
              calendario.
            </Paragraph>

            {gcalConnected ? (
              <Alert
                type="success"
                showIcon
                icon={<CheckCircle2 size={16} />}
                message="Google Calendar conectado"
                style={{ marginBottom: 16 }}
              />
            ) : gcalEnabled ? (
              <Alert
                type="warning"
                showIcon
                icon={<XCircle size={16} />}
                message="No conectado"
                description="Guarda la configuración y luego conecta tu cuenta de Google."
                style={{ marginBottom: 16 }}
              />
            ) : null}

            <Form form={gcalForm} layout="vertical" size="middle">
              <Form.Item
                label="Client ID"
                name="clientId"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <Input placeholder="xxxx.apps.googleusercontent.com" />
              </Form.Item>

              <Form.Item
                label="Client Secret"
                name="clientSecret"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <Input.Password placeholder="GOCSPX-..." />
              </Form.Item>

              <Form.Item
                label="Redirect URI"
                name="redirectUri"
                rules={[{ required: true, message: 'Requerido' }]}
                tooltip="La URL de callback configurada en Google Cloud Console"
              >
                <Input placeholder="https://admin.swomey.cloud/integrations/google-callback" />
              </Form.Item>

              <Form.Item
                label="Calendar ID"
                name="calendarId"
                tooltip="Deja vacío para usar el calendario principal"
              >
                <Input placeholder="primary" />
              </Form.Item>

              {/* Hidden field to preserve refreshToken */}
              <Form.Item name="refreshToken" hidden>
                <Input />
              </Form.Item>
            </Form>

            <Divider style={{ margin: '12px 0' }} />

            <Space>
              <Button
                type="primary"
                icon={<Save size={14} />}
                loading={savingGcal}
                onClick={handleSaveGcal}
                style={{ background: '#1F2F3F' }}
              >
                Guardar
              </Button>
              <Button
                icon={<ExternalLink size={14} />}
                loading={connectingGcal}
                onClick={handleConnectGcal}
                disabled={!gcalEnabled}
              >
                Conectar Google Calendar
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* ── Info Card ── */}
      <Card style={{ marginTop: 24 }}>
        <Title level={5} style={{ marginBottom: 12 }}>
          Formularios con auto-respuesta
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Cuando SMTP está activado, se envía un email de confirmación automático a cada
          persona que envíe un formulario desde la web pública:
        </Paragraph>
        <Space wrap>
          <Tag color="blue">Contacto</Tag>
          <Tag color="blue">Canal de denuncias</Tag>
          <Tag color="blue">Trabaja con nosotros</Tag>
          <Tag color="blue">Colabora como particular</Tag>
          <Tag color="blue">Personal Shopper</Tag>
          <Tag color="blue">Publicación en portales</Tag>
          <Tag color="blue">Valorador online</Tag>
          <Tag color="blue">Modal de contacto</Tag>
        </Space>
      </Card>
    </div>
  );
}
